import React, { useEffect, useState } from "react";
import { Grid, Typography, Paper } from "@mui/material";
import SelectInput from "../../../Components/InputComponents/SelectInput";
import CommonButton from "../../../Components/InputComponents/CommonButton";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  errorNotify,
  infoNotify,
  successNotify,
} from "../../../Components/ToastComponent/ToastNotification";
import { axiosAPI } from "../../../Components/AxiosComponents/Axios";
import { decryptId } from "../../../Components/CryptoComponent";

const EditRecruitmentCountry = () => {
  const { id } = useParams();
  const decryptedId = decryptId(decodeURIComponent(id));
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const redirectURL = `/RecruitmentCountryLookup?${searchParams.toString()}`;

  const [loading, setLoading] = useState(true);
  const [countries, setCountries] = useState();

  const [data, setData] = useState({
    RC_Name: "",
    RC_Status: "",
  });

  const [errors, setErrors] = useState({
    RC_Name: "",
    RC_Status: "",
  });

  const getCountries = () => {
    axiosAPI
      .get(`/RecruitmentCountry/listCountry`)
      .then((res) => {
        setLoading(false);
        const options = res?.data?.data
          .map((item) => ({
            label: item.CL_Name,
            value: item.CL_ID,
          }))
          .sort((a, b) => a.label.localeCompare(b.label));

        setCountries(options);
      })
      .catch((err) => {
        errorNotify(`Something went wrong`);
        setLoading(false);
      });
  };

  const getEditValue = () => {
    axiosAPI
      .get(`/RecruitmentCountry/listbyId/${decryptedId}`)
      .then((res) => {
        setData({
          RC_Name: res?.data?.data?.RC_CountryId,
          RC_Status: res?.data?.data?.RC_Status?.toString(),
        });
      })
      .catch((err) => {
        errorNotify(`Something went wrong`);
      });
  };

  // Validate the form fields
  const validateForm = () => {
    const mandatoryFields = [
      { field: "RC_Name", label: "Recruitment Country" },
      { field: "RC_Status", label: "Status" },
    ];

    let formErrors = {};
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
    const phoneRegex = /^[0-9]{20}$/; // 20-digit phone validation
    for (let { field, label, type } of mandatoryFields) {
      const value = data[field];

      if (!value) {
        formErrors[field] = `${label} is required.`;
      } else if (type === "email" && !emailRegex.test(value)) {
        formErrors[field] = `Please enter a valid ${label}.`;
      } 
    }
    setErrors(formErrors);
    if (Object.keys(formErrors).length > 0) {
      // Display the errors as toast notifications
      Object.values(formErrors).forEach((error) => {
        errorNotify(error);
      });
      return false;
    }
    return true;
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setErrors({
      ...errors,
      [name]: "",
    });

    setData({
      ...data,
      [name]: value,
    });
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      try {
        axiosAPI
          .post(`/RecruitmentCountry/edit/${decryptedId}`, data)
          .then((res) => {
            if (res?.data?.success) {
              successNotify(res?.data?.message);
              navigate(redirectURL);
            } else {
              infoNotify(res?.data?.message);
            }
          })
          .catch((err) => {
            errorNotify("Something went wrong");
          });
      } catch (error) {
        errorNotify("Something went wrong");
      }
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    getEditValue();
    getCountries();
  }, []);

  return (
    <Paper
      sx={{
        maxWidth: `100vw`,
        padding: 2,
        margin: "auto",
        boxShadow: "none",
        overflowX: "auto",
        fontFamily: "Geist, sans-serif",
        minHeight: "100%",
      }}
    >
      <Typography
        variant="h5"
        sx={{
          fontWeight: 600,
          textAlign: "left",
          marginBottom: 3,
          fontFamily: "Geist, sans-serif",
        }}
      >
        Edit Recruitment Country
      </Typography>

      <Grid
        container
        sx={{
          border: "1px solid lightgrey",
          borderRadius: "10px",
          padding: "20px",
        }}
      >
        {/* Recruitment Country Name */}
        <Grid item sx={{ padding: "5px" }} xs={12} sm={6}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
          >
            Recruitment Country Name <span style={{ color: "red" }}> * </span>
          </Typography>
          <SelectInput
            value={data?.RC_Name}
            name="RC_Name"
            onChange={handleInputChange}
            options={countries ? countries : []}
            placeholder="Select a Recruitment Country Name"
            error={!!errors?.RC_Name}
            loading={loading}
          />
        </Grid>

        {/* Status */}
        <Grid item sx={{ padding: "5px" }} xs={12} sm={6}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
          >
            Status <span style={{ color: "red" }}> * </span>
          </Typography>
          <SelectInput
            name="RC_Status"
            value={data?.RC_Status}
            onChange={handleInputChange}
            options={[
              { value: "1", label: "Active" },
              { value: "0", label: "Inactive" },
            ]}
            placeholder="Select Status"
            error={!!errors?.RC_Status}
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* Buttons */}
      <Grid container spacing={2} justifyContent="flex-end" sx={{ mt: 3 }}>
        <Grid item>
          <CommonButton
            variant="outlined"
            customColor="#B0BEC5"
            onClick={() => {
              navigate(`/RecruitmentCountryLookup`);
            }}
          >
            Cancel
          </CommonButton>
        </Grid>
        <Grid item>
          <CommonButton
            variant="contained"
            customColor="#007BFF"
            onClick={handleSubmit}
          >
            Save
          </CommonButton>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default EditRecruitmentCountry;
